import { useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Icon, Table as MuiTable } from "@mui/material";
import TableBody from "@mui/material/TableBody";
import Card from "@mui/material/Card";
import TableRow from "@mui/material/TableRow";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import colors from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";
import React from "react";
import TableCell from "@mui/material/TableCell";
import Collapse from "@mui/material/Collapse";
import KeyboardArrowUpOutlinedIcon from "@mui/icons-material/KeyboardArrowUpOutlined";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";

export function Table({
  columns,
  rows,
  dropDownActive = true,
  handleChangeSort,
  oderByType,
  oderByKey,
}) {
  const [rowResponsiveActive, setRowResponsiveActive] = useState(0);
  const { grey } = colors;
  const { borderWidth } = borders;

  const renderColumns = columns
    .filter((item) => item.responsive.state)
    .sort((a, b) => a.responsive.order - b.responsive.order)
    .map((item) => ({ ...item, showResponsive: true }))
    .concat(columns)
    .map(({ property, label, align, styles, showResponsive }, key) => {
      let pl;
      let pr;

      if (key === 0) {
        pl = 3;
        pr = 3;
      } else if (key === columns.length - 1) {
        pl = 3;
        pr = 3;
      } else {
        pl = 3;
        pr = 1;
      }
      return (
        <VuiBox
          key={uuidv4()}
          component="th"
          pt={1}
          pb={1}
          pl={align === "left" ? pl : 2}
          pr={align === "right" ? pr : 2}
          borderBottom={`${borderWidth[1]} solid ${grey[600]}`}
          color="#FFF"
          sx={({ breakpoints }) => ({
            [breakpoints.down("md")]: {
              display: showResponsive ? "auto" : "none",
              align: "end",
              minWidth: styles?.mobile?.width ?? "auto",
              maxWidth: styles?.mobile?.width ?? "auto",
              pl: "13px",
              fontWeight: "400",
              pr: "0px",
              whiteSpace: "nowrap",
            },
            [breakpoints.up("md")]: {
              display: !showResponsive ? "auto" : "none",
            },
          })}
        >
          <VuiBox sx={{
            display: "flex",
            alignItems: "center",
            gap: "2px",
          }}>
            <VuiBox sx={{
              display: "block",
              width: "100%",
              minWidth: "max-content",
            }}>
              {label}
            </VuiBox>
            {
              handleChangeSort && (
                <VuiBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "column",
                    cursor: "pointer",
                  }}
                >
                  <KeyboardArrowUpOutlinedIcon
                    sx={{
                      color: property === oderByKey && "ASC" === oderByType && "#FFF",
                      "&:hover": { color: "#FFF" },
                      height: "15px",
                    }}
                    onClick={() => handleChangeSort(property, "ASC")}
                  />
                  <KeyboardArrowDownOutlinedIcon
                    sx={{
                      color: property === oderByKey && "DESC" === oderByType && "#FFF",
                      "&:hover": { color: "#FFF" },
                      height: "15px",
                    }}
                    onClick={() => handleChangeSort(property, "DESC")}
                  />
                </VuiBox>
              )
            }
          </VuiBox>
        </VuiBox>
      );
    });

  const renderRows = rows.map((row, key) => {
    const rowKey = `row-${key}`;
    const tableRow = columns
      .filter((item) => item.responsive.state)
      .sort((a, b) => a.responsive.order - b.responsive.order)
      .map((item) => ({ ...item, showResponsive: true }))
      .concat(columns)
      .map(({ property, align, styles, showResponsive }) => (
        <VuiBox
          key={`${property}-${row.challengeAffiliateCodeId}-${showResponsive}`}
          component="td"
          p={1}
          pl={3}
          textAlign={align}
          borderBottom={row.hasBorder ? `${borderWidth[1]} solid ${grey[600]}` : null}
          sx={({ breakpoints }) => ({
            [breakpoints.down("md")]: {
              display: showResponsive ? "auto" : "none",
              align: "end",
              width: styles?.mobile?.width ?? "auto",
              pl: "13px",
            },
            [breakpoints.up("md")]: {
              display: !showResponsive ? "auto" : "none",
            },
          })}
          onClick={() => {
            const id = row.id === rowResponsiveActive ? 0 : row.id;
            setRowResponsiveActive(id);
          }}
        >
          {row[property].default}
        </VuiBox>
      ));

    const dropDownContent = () => {
      const responsiveColumns = columns.filter(({ responsive }) => !responsive.state);
      const mobileUniqueColumn = Object.values(row).filter((item) => item?.special);
      return (
        <React.Fragment>
          <MuiTable aria-label="purchases">
            <VuiBox component="thead">
              <TableRow
                sx={{
                  visibility: "hidden",
                  maxHeight: "0rem",
                  overflow: "hidden",
                }}
              >
                {columns
                  .filter((item) => item.responsive.state)
                  .sort((a, b) => a.responsive.order - b.responsive.order)
                  .map((item) => ({ ...item, showResponsive: true }))
                  .concat(columns)
                  .map(({ styles, showResponsive }) => (
                    <VuiBox
                      key={uuidv4()}
                      component="th"
                      pl={3}
                      sx={({ breakpoints }) => ({
                        height: "1px",
                        pl: "13px",
                        minWidth: styles?.mobile?.width ?? "auto",
                        maxWidth: styles?.mobile?.width ?? "auto",
                        [breakpoints.down("md")]: {
                          display: showResponsive ? "auto" : "none",
                          pl: "13px",
                        },
                        [breakpoints.up("md")]: {
                          display: !showResponsive ? "auto" : "none",
                        },
                      })}
                    ></VuiBox>
                  ))}
              </TableRow>
            </VuiBox>
            <TableBody>
              {responsiveColumns.map((item) => (
                <TableRow key={`mobile-${item.property}`}>
                  <VuiBox
                    component="td"
                    p={1}
                    pl={3}
                    borderBottom={row.hasBorder ? `${borderWidth[1]} solid ${grey[600]}` : null}
                    sx={{
                      pl: "13px",
                      fontSize: "12px",
                    }}
                  >
                    <VuiTypography
                      variant="button"
                      fontWeight="regular"
                      color="white"
                      sx={{ fontSize: "12px" }}
                    >
                      {item.dropDownName} :
                    </VuiTypography>
                  </VuiBox>
                  <VuiBox
                    component="td"
                    colSpan="2"
                    p={1}
                    pl={3}
                    borderBottom={row.hasBorder ? `${borderWidth[1]} solid ${grey[600]}` : null}
                    sx={{
                      width: "100%",
                      padding: "0px",
                      pl: "13px",
                    }}
                  >
                    <VuiTypography
                      variant="button"
                      fontWeight="regular"
                      color="white"
                      sx={{ display: "inline-block", width: "max-content" }}
                    >
                      {row[item.property].default}
                    </VuiTypography>
                  </VuiBox>
                </TableRow>
              ))}
            </TableBody>
          </MuiTable>
          <VuiBox
            sx={{
              width: "80%",
              margin: "auto",
              borderBottom: `${borderWidth[1]} solid ${grey[600]}`,
              padding: mobileUniqueColumn.length && "10px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              gap: ".5rem",
            }}
          >
            {mobileUniqueColumn.map((item) => (
              <React.Fragment key={uuidv4()}>{item.special}</React.Fragment>
            ))}
          </VuiBox>
        </React.Fragment>
      );
    };

    return (
      <React.Fragment key={rowKey}>
        <TableRow>{tableRow}</TableRow>
        {dropDownActive && (
          <TableRow
            sx={({ breakpoints }) => ({
              [breakpoints.up("md")]: {
                display: "none",
              },
            })}
          >
            <TableCell
              style={{ padding: 0, border: "none" }}
              colSpan={columns.filter(({ responsive }) => responsive).length}
            >
              <Collapse in={row.id === rowResponsiveActive} timeout="auto" unmountOnExit>
                {dropDownContent()}
              </Collapse>
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  });

  return useMemo(
    () => (
      <Card
        sx={({ breakpoints }) => ({
          margin: "20px 16px",
          padding: "0px",
          paddingBottom: "20px",
          overflowX: "auto",
          "&::-webkit-scrollbar": {
            width: "7px",
            height: "7px",
          },
          "&::-webkit-scrollbar-track": {
            borderRadius: "7px",
            background: "#eeeeee77",
          },
          "&::-webkit-scrollbar-thumb": {
            borderRadius: "7px",
            background: "#007cff",
          },
          [breakpoints.down("md")]: {
            overflowX: "hidden",
          },
        })}
      >
        <MuiTable>
          <VuiBox component="thead">
            <TableRow>{renderColumns}</TableRow>
          </VuiBox>
          <TableBody>{renderRows}</TableBody>
        </MuiTable>
      </Card>
    ),
    [columns, rows, rowResponsiveActive]
  );
}

export function DefaultTable({ rows, cols }) {
  return (
    <Table
      columns={cols.map((item) => ({
        ...item,
        property: item.property,
        label: (
          <VuiTypography
            color="white"
            sx={{
              display: "flex",
              alignItems: "center",
              fontSize: "14px",
              fontWeight: "400",
            }}
          >
            {item.label}
          </VuiTypography>
        ),
        responsive: item.responsive,
        align: "left",
        styles: item.styles,
        dropDownName: item.label,
      }))}
      rows={rows.map((row) => {
        const mappedRow = {};
        cols.forEach((column) => {
          mappedRow[column.property] = {
            default: (
              <VuiTypography
                color="white"
                sx={{
                  fontSize: "12px",
                  lineHeight: "15.6px",
                  fontWeight: "500",
                  padding: "8px",
                }}
              >
                {row[column.property]?.default ?? row.defaultValue}
              </VuiTypography>
            ),
          };
        });
        return mappedRow;
      })}
    />
  );
}
